import React from 'react';
import PropTypes from 'prop-types';
import {
  FormGroup,
  Row,
  Col,
  Button,
  ButtonToolbar,
} from 'react-bootstrap';

import { Field, reduxForm, formValueSelector, formPropTypes } from 'redux-form';
import { connect } from 'react-redux';
import { compose, lifecycle } from 'recompose';

import DepartmentInput from '../../fields/DepartmentInput';
import DistrictInput from '../../fields/DistrictInput';
import TicketInputEnhanced from '../../fields/TicketInput/containers/TicketInputEnhanced';
import OperatorInput from '../../fields/OperatorInput';
import LeaseInput from '../../fields/LeaseInput';
import JobNumberInput from '../../fields/JobNumberInput/index';
import { createRenderer } from '../../reduxForm/utils';

import '../../../components/App.css';

import Overlay from '../../../components/Overlay';
import { maybeId } from '../../../utils';
import { enableLookupFeature } from '../../configuration/selectors';
import { updateMissingTicketDetailsThunk } from '../actions';

export const FORM_NAME = 'ticketNumberDetailsForm';


const validate = (values, props) => {
  const errors = {};

  if (!values.department) {
    errors.department = 'Required';
  }
  if (!values.district) {
    errors.district = 'Required';
  }
  if (!values.ticket_number) {
    errors.ticket_number = 'Required';
  }
  if (!values.job_number) {
    errors.job_number = 'Required';
  }
  if (!values.operator) {
    errors.operator = 'Required';
  }
  if (!values.lease) {
    errors.lease = 'Required';
  }
  return errors;
};

export const jobEditMessage = 'To edit the opeartor/lease for a job go to the ticket viewer for a ticket on this job and click "edit job"';

const RenderDepartment = createRenderer((input, label, { disabled, districtId }) => {
  return (
    <DepartmentInput districtId={districtId} disabled={disabled} {...input} />
  );
});

const RenderDistrict = createRenderer((input, label, { disabled, departmentId }) => {
  return (
    <DistrictInput departmentId={departmentId} disabled={disabled} {...input} />
  );
});

const RenderTicket = createRenderer((input, label, { disabled }) => {
  return (
    <Overlay enabled message="Not able to change">
      <TicketInputEnhanced disabled={disabled} assigned="unassigned" {...input} />
    </Overlay>
  );
});

const RenderOperator = createRenderer((input, label, { disabled, enableLookup, hasJob }) => {
  return (
    <Overlay enabled={hasJob} message={jobEditMessage}>
      <OperatorInput
        lookupJoinType="user_records"
        enableLookup={enableLookup}
        disabled={disabled}
        {...input}
      />
    </Overlay>
  );
});

const RenderLease = createRenderer((input, label, { disabled, operator, lookupJoinType, hasJob }) => {
  return (
    <Overlay enabled={hasJob} message={jobEditMessage}>
      <LeaseInput
        operator={operator}
        disabled={disabled}
        lookupJoinType={lookupJoinType}
        {...input}
      />
    </Overlay>
  );
});


const RenderJobNumberInput = createRenderer((input, label, { disabled }) => {
  return (
    <Overlay enabled message="Unable to change job number: Make the changes during submission.">
      <JobNumberInput disabled={disabled} {...input} />
    </Overlay>
  );
});


/* eslint-disable react/forbid-prop-types, react/require-default-props */
const propTypes = {
  onHide: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  departmentObject: PropTypes.object,
  leaseObject: PropTypes.object,
  operatorObject: PropTypes.object,
  enableLookup: PropTypes.bool.isRequired,
  ...formPropTypes,
};
/* eslint-enable */

const TicketEntry = (props) => {
  const {
    handleSubmit,
    submitting,
    pristine,
    operatorObject,
    invalid,
    districtId,
    departmentId,
    error,
    lookupJoinType,
    enableLookup,
    jobNumberObject,
    updateMissingTicketDetailsAction,
  } = props;

  const onSubmit = async (values) => {
    const {
      ticket_number: ticketNumber,
      operator,
      lease,
      district,
      department,
    } = values;

    // NOTE: operator and lease are using lookup data
    const payloadObject = {
      ticket_number_id: ticketNumber.id,
      operator_id: (operator.user_id) ? operator.user_id : operator.id,
      lease_id: (lease.user_id) ? lease.user_id : lease.id,
      district_id: district.id,
      department_id: department.id,
    };

    await updateMissingTicketDetailsAction(payloadObject, props.onHide);
  };

  const hasJob = !!jobNumberObject && !!jobNumberObject.hasJob;

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Field
                  name="ticket_number"
                  type="input"
                  label="Ticket Number"
                  format={null}
                  disabled
                  component={RenderTicket}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Field
                  name="job_number"
                  type="select"
                  label="Job Number"
                  format={null}
                  disabled
                  component={RenderJobNumberInput}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Field
                  departmentId={departmentId}
                  name="district"
                  type="select"
                  label="District"
                  format={null}
                  component={RenderDistrict}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Field
                  districtId={districtId}
                  name="department"
                  type="select"
                  label="Department"
                  format={null}
                  component={RenderDepartment}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Field
                  disabled={hasJob}
                  name="operator"
                  type="select"
                  label="Operator"
                  format={null}
                  hasJob={hasJob}
                  component={RenderOperator}
                  enableLookup={enableLookup}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Field
                  disabled={hasJob}
                  operator={operatorObject}
                  name="lease"
                  type="select"
                  label="Lease"
                  format={null}
                  hasJob={hasJob}
                  component={RenderLease}
                  lookupJoinType={lookupJoinType}
                />
              </FormGroup>
            </Col>
          </Row>
        </FormGroup>
        <Row>
          <Col md={12}>
            <ButtonToolbar className="pull-right">
              <Button bsStyle="warning" onClick={props.onHide}>
                Cancel
              </Button>

              <Button
                bsStyle="primary"
                type="submit"
                disabled={(submitting || pristine || invalid)}
              >
                Update
              </Button>

            </ButtonToolbar>
          </Col>
        </Row>
        {error && (
          <span>
            <strong>{error}</strong>
          </span>
        )}
      </form>
    </div>
  );
};


TicketEntry.propTypes = propTypes;

const withForm = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  validate,
});

const selector = formValueSelector(FORM_NAME);

const mapDispatchToProps = {
  updateMissingTicketDetailsAction: updateMissingTicketDetailsThunk,
};

const withRedux = connect((state) => {
  const jobNumberObject = selector(state, 'job_number');
  const departmentObject = selector(state, 'department');
  const operatorObject = selector(state, 'operator');
  const leaseObject = selector(state, 'lease');
  const district = selector(state, 'district');
  const districtId = maybeId(district);
  const departmentId = maybeId(departmentObject);
  const lookupJoinType = enableLookupFeature(state) ? 'union' : 'user_records';
  const enableLookup = enableLookupFeature(state);

  return {
    departmentObject,
    operatorObject,
    leaseObject,
    districtId,
    departmentId,
    lookupJoinType,
    enableLookup,
    jobNumberObject,
  };
}, mapDispatchToProps);

const handleFieldChangeRelationship = lifecycle({
  componentDidMount() {
    const {
      district,
      department,
      operator,
      lease,
    } = this.props.initialValues;

    this.props.change('district', district);
    this.props.change('department', department);
    this.props.change('operator', operator);
    this.props.change('lease', lease);
  },
  componentWillReceiveProps(nextProps) {
    /* NOTE: with job number being disabled and not changable, the only after affect is
    setting lease to null when the operator changes */
    const {
      operatorObject: nextOperatorObject,
    } = nextProps;

    const {
      operatorObject: prevOperatorObject,
    } = this.props;

    if (nextOperatorObject === null) {
      this.props.change('lease', null);
    }

    // if operator has changed, then the lease needs to be null
    // comparing two lookups
    if (prevOperatorObject !== null && nextOperatorObject !== null) {
      if (prevOperatorObject.user_id !== nextOperatorObject.user_id) {
        this.props.change('lease', null);
      }
    }
  },
});

const enhance = compose(
  withForm,
  withRedux,
  handleFieldChangeRelationship,
);


export default enhance(TicketEntry);
